import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { Country, type ICountry, type IState, State } from "country-state-city";
import { useMemo, useState } from "react";

/** The bill-to address fields (matches CreateBillingAddressBody). Country/State are canonical names
 *  picked from a dataset — never free-typed — so they can't be misspelled or mismatched. */
export interface AddressFormValues {
  label: string;
  contactName: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const EMPTY: AddressFormValues = {
  label: "",
  contactName: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
};

/**
 * Add-address form for the bill-to picker. Country and State/Province are cascading Autocompletes from
 * `country-state-city` (offline dataset) — State only lists provinces of the chosen country, so wrong
 * names and invalid country/state combos are impossible. City + postal stay free text (city datasets
 * are huge/incomplete; postal formats vary by country).
 */
export default function AddressForm({
  onSave,
  onCancel,
  isSaving,
}: {
  onSave: (values: AddressFormValues) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<AddressFormValues>(EMPTY);
  const [country, setCountry] = useState<ICountry | null>(null);
  const [state, setState] = useState<IState | null>(null);

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (country ? State.getStatesOfCountry(country.isoCode) : []),
    [country],
  );

  const set = (key: keyof AddressFormValues, value: string) =>
    setForm((s) => ({ ...s, [key]: value }));

  const textField = (key: keyof AddressFormValues, label: string, extra?: object) => (
    <TextField
      size="small"
      label={label}
      value={form[key]}
      onChange={(e) => set(key, e.target.value)}
      {...extra}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
      {textField("label", "Label")}
      {textField("contactName", "Contact name")}
      {textField("email", "Email")}
      {textField("addressLine1", "Address")}
      {textField("addressLine2", "Address line 2 (optional)")}
      {textField("city", "City")}

      <Autocomplete<ICountry>
        options={countries}
        value={country}
        getOptionLabel={(c) => c.name}
        isOptionEqualToValue={(a, b) => a.isoCode === b.isoCode}
        onChange={(_, c) => {
          setCountry(c);
          setState(null);
          setForm((s) => ({ ...s, country: c?.name ?? "", state: "" }));
        }}
        size="small"
        renderInput={(p) => <TextField {...p} label="Country" />}
      />
      <Autocomplete<IState>
        options={states}
        value={state}
        disabled={!country}
        getOptionLabel={(s) => s.name}
        isOptionEqualToValue={(a, b) => a.isoCode === b.isoCode}
        onChange={(_, s) => {
          setState(s);
          setForm((f) => ({ ...f, state: s?.name ?? "" }));
        }}
        size="small"
        renderInput={(p) => (
          <TextField {...p} label={country ? "State / Province" : "State (pick a country first)"} />
        )}
      />
      {textField("postalCode", "Postal code")}

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button variant="contained" onClick={() => onSave(form)} disabled={isSaving}>
          Save address
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </Box>
    </Box>
  );
}
